import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { db, eq, ilike } from "../lib/api";
import { invalidate, useDebounced, useQuery } from "../lib/hooks";
import { Field, Sheet, useToast } from "./ui";
import { IconBell } from "./icons";
import { openPerson } from "./PersonDrawer";
import { navigate } from "../lib/router";

type Due = { id: string; at: string; note: string; person_id: string | null; person: string | null; opportunity_id: string | null; deal: string | null; task_id: string | null; task: string | null };
type BellData = { due: Due[]; later: { id: string; at: string; note: string; person: string | null }[];
  meetings: { id: string; at: string; title: string; person_id: string | null; person: string | null; link: string | null }[]; overdue_tasks: number };

/** The bell (top right): your own reminders. Due ones ring; the next 7 days are listed. Checks every minute. */
export function Bell() {
  const q = useQuery("bell", () => db.rpc<BellData>("bell"), { staleMs: 55_000 });
  const [open, setOpen] = useState(false);
  const toast = useToast();
  useEffect(() => { const t = setInterval(() => invalidate("bell"), 60_000); return () => clearInterval(t); }, []);
  const d = q.data;
  const count = d?.due.length ?? 0;
  async function act(id: string, patch: Record<string, unknown>, msg: string) {
    await db.update("my_reminders", { id: eq(id) }, patch); invalidate("bell"); toast(msg);
  }
  const at = (s: string) => dayjs(s).isSame(dayjs(), "day") ? dayjs(s).format("h:mm A") : dayjs(s).format("MMM D, h:mm A");
  return (
    <>
      <button className="bell-btn" onClick={() => setOpen((x) => !x)} aria-label={`Reminders${count ? ` (${count})` : ""}`} title="Reminders">
        <IconBell size={20} />{count > 0 && <span className="bell-n">{count > 9 ? "9+" : count}</span>}
      </button>
      {open && <>
        <div className="bell-away" onClick={() => setOpen(false)} />
        <div className="bell-pop" role="dialog" aria-label="Reminders">
          <div className="bell-head"><b>Reminders</b><RemindMeButton small label="+ New" /></div>
          {!d ? <p className="small muted">Loading…</p> : <>
            {d.due.map((r) => (
              <div key={r.id} className="bell-item">
                <div><b>{r.note || r.person || r.task || "Reminder"}</b>
                  <div className="small muted">{[r.person && r.note ? r.person : null, r.task ? `Linked task: ${r.task}` : null, r.deal ? `Deal: ${r.deal}` : null, at(r.at)].filter(Boolean).join(" · ")}</div></div>
                <div className="bell-acts">
                  {r.person_id && <button className="btn small" onClick={() => { setOpen(false); openPerson(r.person_id!); }}>Open</button>}
                  {!r.person_id && r.task_id && <button className="btn small" onClick={() => { setOpen(false); navigate("/tasks"); }}>Tasks</button>}
                  <button className="btn small" title="Remind me again in an hour" onClick={() => act(r.id, { remind_at: dayjs().add(1, "hour").toISOString(), emailed_at: null }, "Snoozed for an hour")}>1 h</button>
                  <button className="btn small" title="Tomorrow at 9 AM" onClick={() => act(r.id, { remind_at: dayjs().add(1, "day").hour(9).minute(0).second(0).toISOString(), emailed_at: null }, "Snoozed to tomorrow 9 AM")}>Tomorrow</button>
                  <button className="btn small primary" onClick={() => act(r.id, { done_at: new Date().toISOString() }, "Done")}>Done</button>
                </div>
              </div>))}
            {!d.due.length && <p className="small muted" style={{ margin: "6px 0" }}>Nothing to remember right now.</p>}
            {d.later.length > 0 && <div className="bell-later"><div className="small muted" style={{ fontWeight: 600 }}>Coming up (7 days)</div>
              {d.later.map((r) => <div key={r.id} className="small">{at(r.at)} · {r.note || r.person || "Reminder"}{r.note && r.person ? ` (${r.person})` : ""}</div>)}</div>}
          </>}
        </div>
      </>}
    </>
  );
}

/** "Remind me": a reminder for you only, on a person, deal or task (or just a note). */
export function RemindMeButton({ person, opportunityId, task, small, label = "Remind me" }:
  { person?: { id: string; full_name: string }; opportunityId?: string; task?: { id: string; title: string }; small?: boolean; label?: string }) {
  const [open, setOpen] = useState(false);
  return (<>
    <button className={small ? "btn small" : "btn"} onClick={() => setOpen(true)} title="Set a reminder for yourself"><IconBell size={16} />{label}</button>
    {open && <RemindMeSheet person={person} opportunityId={opportunityId} task={task} onClose={() => setOpen(false)} />}
  </>);
}

function RemindMeSheet({ person: person0, opportunityId, task: task0, onClose }: { person?: { id: string; full_name: string }; opportunityId?: string; task?: { id: string; title: string }; onClose: () => void }) {
  const toast = useToast();
  const [person, setPerson] = useState(person0);
  const [task, setTask] = useState(task0);
  const [linkTask, setLinkTask] = useState(false);
  const [linkPerson, setLinkPerson] = useState(false);
  const [pq, setPq] = useState("");
  const dq = useDebounced(pq);
  const tasks = useQuery(linkTask ? "bell:mytasks" : null, () => db.select<{ id: string; title: string; due_on: string | null }>("tasks", { status: "neq.Done", select: "id,title,due_on", order: "due_on.asc.nullslast", limit: 200 }));
  const found = useQuery(linkPerson && dq.length > 1 ? `bell:find:${dq}` : null, () => db.select<{ id: string; full_name: string }>("people", { select: "id,full_name", full_name: ilike(dq), deleted_at: "is.null", limit: 6 }));
  const [f, setF] = useState({ at: dayjs().add(1, "day").hour(9).minute(0).format("YYYY-MM-DDTHH:mm"), note: "", email: false });
  const [err, setErr] = useState("");
  const quick: [string, () => dayjs.Dayjs][] = [["In 1 hour", () => dayjs().add(1, "hour")], ["This evening", () => dayjs().hour(17).minute(0)],
    ["Tomorrow 9 AM", () => dayjs().add(1, "day").hour(9).minute(0)], ["Next Monday", () => dayjs().add(((8 - dayjs().day()) % 7) || 7, "day").hour(9).minute(0)], ["In 2 weeks", () => dayjs().add(14, "day").hour(9).minute(0)]];
  async function save() {
    setErr("");
    try {
      if (!f.at) throw new Error("Choose when.");
      await db.insert("my_reminders", { remind_at: dayjs(f.at).toISOString(), note: f.note.trim(), email: f.email,
        person_id: person?.id ?? null, opportunity_id: opportunityId ?? null, task_id: task?.id ?? null });
      invalidate("bell"); toast(`Reminder set for ${dayjs(f.at).format("ddd, MMM D h:mm A")}`); onClose();
    } catch (e: any) { setErr(e.message ?? String(e)); }
  }
  return (
    <Sheet title="New reminder" onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" onClick={save}>Set reminder</button></>}>
      <div className="chips" style={{ marginBottom: 10 }}>{quick.map(([l, fn]) => (
        <button key={l} type="button" className="chip" onClick={() => setF({ ...f, at: fn().format("YYYY-MM-DDTHH:mm") })}>{l}</button>))}</div>
      <div className="form-grid">
        <Field label="When"><input type="datetime-local" className="input" value={f.at} onChange={(e) => setF({ ...f, at: e.target.value })} /></Field>
        <Field label="What do you need to remember?" wide><textarea className="textarea" rows={3} autoFocus value={f.note} placeholder="e.g. Send Ada the RESP form" onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
        <Field label="Linked task (optional)" wide>
          {task ? <div className="row-inline" style={{ margin: 0 }}><span>{task.title}</span><button className="btn small ghost" onClick={() => setTask(undefined)}>Unlink</button></div>
            : !linkTask ? <div><button className="btn small" type="button" onClick={() => setLinkTask(true)}>Link to a task</button></div>
            : <select className="input" value="" onChange={(e) => { const t = tasks.data?.find((x) => x.id === e.target.value); if (t) setTask({ id: t.id, title: t.title }); }}>
                <option value="">{tasks.loading ? "Loading your tasks…" : "Choose a task"}</option>
                {(tasks.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.title}{t.due_on ? ` · due ${dayjs(t.due_on).format("MMM D")}` : ""}</option>)}
              </select>}
        </Field>
        <Field label="About someone (optional)" wide>
          {person ? <div className="row-inline" style={{ margin: 0 }}><span>{person.full_name}</span>{!person0 && <button className="btn small ghost" onClick={() => setPerson(undefined)}>Remove</button>}</div>
            : !linkPerson ? <div><button className="btn small" type="button" onClick={() => setLinkPerson(true)}>Link to a person</button></div>
            : <><input className="input" value={pq} placeholder="Search a name" onChange={(e) => setPq(e.target.value)} />
                {(found.data ?? []).map((p) => <button key={p.id} type="button" className="btn ghost small" style={{ justifyContent: "flex-start" }} onClick={() => { setPerson(p); setPq(""); }}>{p.full_name}</button>)}</>}
        </Field>
      </div>
      <label className="check"><input type="checkbox" checked={f.email} onChange={(e) => setF({ ...f, email: e.target.checked })} /> Also email me when it's due</label>
      <p className="small muted">A reminder isn't a task: it's something for you to remember. Only you see it; it rings on the bell at the top right when it's due.</p>
      {err && <p className="error-text">{err}</p>}
    </Sheet>
  );
}
